// @flow
import React from 'react';
import { withRouter } from 'react-router-dom';
import { map, find, get, isEqual } from 'lodash';
import Tabs, { TabPane } from 'rc-tabs';
import TabContent from 'rc-tabs/lib/TabContent';
import SwipeableInkTabBar from 'rc-tabs/lib/SwipeableInkTabBar';
import classnames from 'classnames';
import './SwipeTabs.scss';

type Item = {
  title?: ?string,
  text?: ?string,
  linkTo?: string,
  value: string | number,
};

type Props = {
  items: Array<Item>,
  active?: string | number,
  className?: string,
  id?: string,
  pageSize?: number,
  onTabChange?: Function,
  history: Object, // from withRouter
};

const makeMultiTabPane = (items) => {
  const result = map<any, any>(items, (item) => (
    <TabPane
      tab={
        <div data-extra="tab-bar-title">
          {`${get(item, 'title', item.text)}`}
        </div>
      }
      data-extra="tabpane"
      key={`${get(item, 'title', item.text)}`}
      link={item.linkTo}
    />
  ));
  return result;
};

// Tabs that are pages (the patient's Cadastro, Prontuário and Financeiro) mount a new bar on every switch, so the
// underline used to appear under the new tab at once. The bar that leaves notes where its active tab was; the one
// that mounts right after, for the same tabs, slides its underline from there (transform only, on the platform's
// curve). Measured on the active tab, not the underline, so a slide still running doesn't count.
type Spot = { key: string, left: number, width: number, at: number };
let leaving: ?Spot = null;
const FRESH = 1000; // ms: a bar mounted later than this (another visit) doesn't slide

export const forgetUnderline = () => { leaving = null; };

const underlineKey = (items: Array<Item>) => (
  items.length && items.every((item) => item.linkTo) ? items.map((item) => item.linkTo).join('|') : null
);

// The keyframes that take the underline from the old tab (`from`) to where it is (`to`). rc-tabs places it with
// transform: translate3d(offset) from transform-origin 0 0, and the scale property applies around that same
// origin, so a stretch also scales the offset: the translate makes up for it.
export const underlineSlide = (from: ?{ left: number, width: number }, to: { left: number, width: number }, offset: number = 0) => {
  if (!from || !to.width || !from.width) return null;
  if (Math.abs(from.left - to.left) < 1 && Math.abs(from.width - to.width) < 1) return null;
  const scale = from.width / to.width;
  const dx = (from.left - to.left) + (offset * (1 - scale));
  return [
    { translate: `${Math.round(dx)}px 0`, scale: `${scale.toFixed(3)} 1` },
    { translate: '0px 0', scale: '1 1' },
  ];
};

const canSlide = () => (
  Boolean(document.getElementById('mobileApp')) &&
  typeof window.CSS !== 'undefined' && typeof window.CSS.supports === 'function' && window.CSS.supports('translate', '1px') &&
  !(typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
);

// The x of rc-tabs' own transform on the underline
const offsetOf = (el: HTMLElement) => {
  const t = window.getComputedStyle(el).transform;
  if (!t || t === 'none') return 0;
  const values = t.slice(t.indexOf('(') + 1, -1).split(',').map(Number);
  const x = t.startsWith('matrix3d') ? values[12] : values[4];
  return Number.isFinite(x) ? x : 0;
};

class SwipeTabs extends React.Component<Props> {
  componentDidMount() {
    // rc-tabs places the underline in a timeout of its own, which runs first
    this.underlineTimer = setTimeout(this.slideUnderline, 0);
  }

  shouldComponentUpdate(nextProps) {
    return !isEqual(this.props, nextProps);
  }

  componentWillUnmount() {
    clearTimeout(this.underlineTimer);
    const key = underlineKey(this.props.items);
    const tab = key && this.activeTab();
    leaving = tab ? { key, ...tab, at: Date.now() } : null;
  }

  render() {
    const {
      items,
      className,
      id,
      pageSize,
      active,
    } = this.props;

    const cssClasses = classnames(['swipeTabs', className]);

    const activeTab = active || (items.length > 0 ? get(items[0], 'title', items[0].text) : null);

    return (
      <div id={id} className={cssClasses} ref={this.setRoot}>
        <Tabs
          onChange={this.onTabChange.bind(this)}
          data-extra="tabs"
          renderTabBar={() => (
            <SwipeableInkTabBar
              pageSize={pageSize || 2}
              speed={2}
              data-extra="tabbar"
            />

          )}
          renderTabContent={() => <TabContent />}
          defaultActiveKey={activeTab}
        >
          {makeMultiTabPane(items)}
        </Tabs>
      </div>
    );
  }

  root: ?HTMLElement = null;

  underlineTimer: any = null;

  setRoot = (el: ?HTMLElement) => { this.root = el; };

  activeTab = () => {
    const tab = this.root && this.root.querySelector('.rc-tabs-tab-active');
    if (!tab) return null;
    const rect = tab.getBoundingClientRect();
    return rect.width ? { left: rect.left, width: rect.width } : null;
  };

  slideUnderline = () => {
    const key = underlineKey(this.props.items);
    const from = leaving;
    leaving = null;
    if (!key || !from || from.key !== key || Date.now() - from.at > FRESH || !canSlide()) return;
    const ink = this.root && this.root.querySelector('.rc-tabs-ink-bar');
    const to = this.activeTab();
    // rc-tabs places the underline in a timeout of its own, which runs first; not placed: no slide
    if (!ink || !to || !parseFloat(ink.style.width) || typeof ink.animate !== 'function') return;
    const keyframes = underlineSlide(from, to, offsetOf(ink));
    if (!keyframes) return;
    const ios = document.documentElement && document.documentElement.classList.contains('iosApp');
    ink.animate(keyframes, { duration: 250, easing: ios ? 'cubic-bezier(.32, .72, 0, 1)' : 'cubic-bezier(.2, 0, 0, 1)' });
  };

  onTabChange = (value: string | number) => {
    const item = find(this.props.items, (obj) => (get(obj, 'title', obj.text) === value));
    if (item && item.linkTo) {
      this.props.history.push(item.linkTo);
    }
    if (this.props.onTabChange && item) {
      this.props.onTabChange(item.value);
    }
  }
}
export default withRouter(SwipeTabs);
